/* eslint-disable jsx-a11y/anchor-is-valid */
import clsx from 'clsx'
import { useFormik } from 'formik'
import { useState } from 'react'
import { Link } from 'react-router-dom'
import * as Yup from 'yup'
import { AlertModal } from '../../../common/components/AlertModal'
import { useAuth } from '../core/Auth'
import { encodeLoginCredentials } from '../core/loginKeyHex'
import { login } from '../core/_requests'
import { useGoogleRecaptcha } from '../hooks/useGoogleRecaptcha'
import { getErrorMessage, getErrorModalIcon, getErrorModalTitle, shouldShowModal } from '../utils/errorDictionary'

const loginSchema = Yup.object().shape({
  email: Yup.string()
    .email('Formato de correo invalido')
    .required('Correo electrónico es requerido'),
  password: Yup.string().required('Contraseña es requerida'),
})

const initialValues = {
  email: '',
  password: '',
}

export function Login() {
  const [loading, setLoading] = useState(false)
  const [showErrorModal, setShowErrorModal] = useState(false)
  const [errorModalMessage, setErrorModalMessage] = useState('')
  const [errorModalTitle, setErrorModalTitle] = useState('Revisa tu bandeja de entrada')
  const [errorModalIcon, setErrorModalIcon] = useState<'success' | 'error' | 'warning' | 'info' | 'message'>('message')
  const { saveAuth, setCurrentUser, savePermissions } = useAuth()
  const { handleReCaptchaVerify } = useGoogleRecaptcha()

  const formik = useFormik({
    initialValues,
    validationSchema: loginSchema,
    onSubmit: async (values, { setStatus, setSubmitting }) => {
      setLoading(true)
      try {
        await handleReCaptchaVerify('login')
        const encodedPassword = values.password
          ? encodeLoginCredentials(values.password)
          : values.password
        const { data: auth } = await login(values.email, encodedPassword)

        if (auth.user.isPasswordExpired|| auth.user.verifyFirstPassword || auth.user.forcePasswordChange) {
          setErrorModalMessage('Hemos enviado un correo con un enlace al que debes acceder para cambiar tu contraseña.')
          setErrorModalTitle('Revisa tu bandeja de entrada')
          setErrorModalIcon('message')
          setShowErrorModal(true)
          setSubmitting(false)
          setLoading(false)
          return
        }

        saveAuth(auth)
        setCurrentUser(auth.user)
        savePermissions(auth.user.roles)
      } catch (error: any) {
        // Extraer el mensaje de error de diferentes estructuras posibles
        let errorMessage = ''
        
        // Intentar extraer de error.response.data.message
        if (error?.response?.data?.message) {
          errorMessage = error.response.data.message
        }
        // Si response.data es un string directamente
        else if (error?.response?.data && typeof error.response.data === 'string') {
          errorMessage = error.response.data
        }
        // Intentar extraer de error.message
        else if (error?.message) {
          if (typeof error.message === 'string') {
            errorMessage = error.message
          } else if (error.message.message && typeof error.message.message === 'string') {
            errorMessage = error.message.message
          }
        }
        
        console.log('Error de login:', errorMessage) // Log para depuración
        
        if (shouldShowModal(errorMessage)) {
          setErrorModalMessage(getErrorMessage(errorMessage))
          setErrorModalTitle(getErrorModalTitle(errorMessage))
          setErrorModalIcon(getErrorModalIcon(errorMessage))
          setShowErrorModal(true)
        } else {
          setStatus(getErrorMessage(errorMessage))
        }

        saveAuth(undefined)
        setSubmitting(false)
        setLoading(false)
      }
    },
  })

  return (
    <form
      className='form w-100'
      onSubmit={formik.handleSubmit}
      noValidate
      id='kt_login_signin_form'
    >
      <div className='text-center mb-6'>
        <img src='/media/logos/prudencialsafblue.svg' alt='logo' style={{ maxWidth: '220px' }} />
      </div>

      {formik.status && (
        <div className='mb-lg-15 alert alert-danger'>
          <div className='alert-text font-weight-bold'>{formik.status}</div>
        </div>
      )}

      {/* begin::Form group */}
      <div className='fv-row mb-10 mt-10'>
        <label className='form-label fs-6 fw-bolder text-dark'>Correo electrónico</label>
        <input
          placeholder='Correo electrónico'
          {...formik.getFieldProps('email')}
          className={clsx(
            'form-control form-control-lg form-control-solid',
            { 'is-invalid': formik.touched.email && formik.errors.email },
            {
              'is-valid': formik.touched.email && !formik.errors.email,
            }
          )}
          type='email'
          name='email'
          autoComplete='off'
        />
        {formik.touched.email && formik.errors.email && (
          <div className='fv-plugins-message-container'>
            <span className='text-danger' role='alert'>
              {formik.errors.email}
            </span>
          </div>
        )}
      </div>
      {/* end::Form group */}

      {/* begin::Form group */}
      <div className='fv-row mb-10'>
        <div className='d-flex justify-content-between mt-n5'>
          <div className='d-flex flex-stack mb-2'>
            {/* begin::Label */}
            <label className='form-label fw-bolder text-dark fs-6 mb-0'>Contraseña</label>
            {/* end::Label */}
            {/* begin::Link */}

            {/* end::Link */}
          </div>
        </div>
        <input
          type='password'
          placeholder='Contraseña'
          autoComplete='off'
          {...formik.getFieldProps('password')}
          className={clsx(
            'form-control form-control-lg form-control-solid',
            {
              'is-invalid': formik.touched.password && formik.errors.password,
            },
            {
              'is-valid': formik.touched.password && !formik.errors.password,
            }
          )}
        />
        {formik.touched.password && formik.errors.password && (
          <div className='fv-plugins-message-container'>
            <div className='fv-help-block'>
              <span className='text-danger' role='alert'>
                {formik.errors.password}
              </span>
            </div>
          </div>
        )}
      </div>
      {/* end::Form group */}

      {/* begin::Action */}
      <div className='text-center'>
        <button
          style={{ background: '#007BC3', color: 'white', maxWidth: '320px' }}
          type='submit'
          id='kt_sign_in_submit'
          className='btn btn-lg btn-dark w-100 mb-5'
          disabled={formik.isSubmitting || !formik.isValid}
        >
          {!loading && <span className='indicator-label'>Iniciar sesión</span>}
          {loading && (
            <span className='indicator-progress' style={{ display: 'block' }}>
              Espera un momento...
              <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
            </span>
          )}
        </button>
        <Link
          to='/auth/forgot-password'
          className='fs-6 fw-bolder d-block'
          style={{ color: '#007BC3', textDecoration: 'none' }}
        >
          Olvidé mi contraseña
        </Link>
      </div>
      {/* end::Action */}

      {/* Modal de error para cuenta bloqueada o contraseña expirada */}
      <AlertModal
        show={showErrorModal}
        onHide={() => {
          setShowErrorModal(false)
        }}
        icon={errorModalIcon}
        title={errorModalTitle}
        message={errorModalMessage}
        buttonText='Aceptar'
        maxWidth={420}
        fullWidthButton={true}
        onConfirm={() => {
          setShowErrorModal(false)
        }}
      />
    </form>
  )
}
